import { Children, createContext, isValidElement, type ReactNode, useContext } from "react";
import { Badge, Body1, Button, type ButtonProps, Caption, Display2, GrantedGLogo, getTokenValue, YStack } from "@medbillai/ui";
import type { ShallowDistributiveOmit } from "@medbillai/utils";
import { NewGLogo } from "../local-images";
import { OnboardingProgressHeader } from "./OnboardingProgressHeader";

// Platform-neutral pieces of the OnboardingScreen compound component. The
// platform mechanics (native keyboard handling vs. web page scrolling) live
// in OnboardingScreen.native.tsx / OnboardingScreen.tsx, which compose these
// and must expose an identical public API.

// Resolve at module-load — Tamagui's native `Image` doesn't reliably
// parse `$`-prefixed size tokens, so pass it a concrete number.
export const LOGO_SIZE = Number(getTokenValue("$si5", "size"));
export const FOOTER_KEYBOARD_OFFSET = Number(getTokenValue("$-sp2", "space"));
// One token for the footer slot gap so the layout and computeOpenedOffset can't drift.
export const FOOTER_SLOT_GAP_TOKEN = "$sp2" as const;
export const SLOT_GAP = Number(getTokenValue(FOOTER_SLOT_GAP_TOKEN, "space"));

/**
 * Surface the screen paints on. `"dark"` is the brand green surface: the root
 * fills with `$surfaceInverse` and every slot that owns a colour switches to
 * the inverse foreground ramp, so a dark screen never picks its own values.
 */
export type OnboardingTone = "light" | "dark";
export const OnboardingToneContext = createContext<OnboardingTone>("light");

// `as const` keeps the token a literal; a widened `string` isn't assignable to
// Tamagui's backgroundColor.
export const TONE_BACKGROUND = {
  light: undefined,
  dark: "$surfaceInverse"
} as const satisfies Record<OnboardingTone, string | undefined>;

// Web only — a same-colour 1px ring, so it costs no layout.
export const TONE_BLEED_GUARD = {
  light: undefined,
  dark: "0 0 0 1px $surfaceInverse"
} as const satisfies Record<OnboardingTone, string | undefined>;
type TextButtonProps = Extract<ButtonProps, {
  children: ReactNode;
}>;
export type OnboardingButtonProps = ShallowDistributiveOmit<TextButtonProps, "variant" | "fillWidth" | "size">;
export type OnboardingNavProps = {
  /** 0–100. When omitted, the progress bar is hidden. */
  progress?: number;
  /** Native only — web always shows the wordmark. Pass to render a back button. */
  onBackPress?: () => void;
  /** Accessible title; not rendered visibly. */
  title?: string;
  /** Web only — render the Granted wordmark in the left slot. @default true */
  showLogo?: boolean;
  /** Overrides the root's tone for the nav alone. Rarely needed. */
  tone?: OnboardingTone;
  /** When provided, shows a close button in the trailing slot. */
  onClosePress?: () => void;
};
type OnboardingHeaderBaseProps = {
  title: string;
  /** Plain text, or inline text spans (e.g. a bolded email) rendered inside the Body1. */
  subtitle?: ReactNode;
  /** Flex alignment and `textAlign` for title/subtitle. @default "center" */
  alignment?: "center" | "left";
  testID?: string;
};
export type OnboardingHeaderProps = OnboardingHeaderBaseProps & ({
  showLogo?: boolean;
  badge?: never;
} | {
  showLogo?: never;
  badge?: string;
});
export type OnboardingContentProps = {
  children: ReactNode;
};
export type OnboardingFooterProps = {
  /** Web-only fade; matches the opacity/transition pattern used for content. */
  isFading?: boolean;
  children: ReactNode;
};
export type OnboardingPrimaryActionProps = OnboardingButtonProps & {
  /** @default "primary" */
  variant?: "primary" | "brand";
  /** A right-aligned pill that hugs its label instead of filling the footer; the slot still measures it for the keyboard offset. */
  compact?: boolean;
};
export type OnboardingDisclaimerProps = {
  children: ReactNode;
};
export type OnboardingFormErrorProps = {
  message: string | null;
};
export type OnboardingScreenProps = {
  /** Render `BackgroundRadialGrantedGradient` fullscreen behind the content. */
  showBackgroundGradient?: boolean;
  /**
   * Surface to paint. `"dark"` fills the root with `$surfaceInverse` and puts
   * Nav and Header on the inverse foreground ramp. Put it on the root rather
   * than a wrapper: the root is what owns the viewport sizing, so a background
   * on anything outside it stops at content height.
   * @default "light"
   */
  tone?: OnboardingTone;
  /**
   * Web only — pin the footer to the visible bottom via a viewport min-height
   * floor on the root. Every viewport-height surface (onboarding, auth,
   * harnesses) keeps the default; pass `false` when the screen is embedded
   * under other page chrome (e.g. FakeDoorMmfaStep below the site navbar),
   * where the floor would overflow the host. Native lays out its own way and
   * ignores this.
   * @default true
   */
  pinnedFooter?: boolean;
  /**
   * Web only — cap the screen at exactly the viewport height instead of the
   * page-scroll floor. Content scrolls between the nav and the footer, so the
   * footer stays visible; a bounded scroller inside Content (e.g. a
   * search-result list) absorbs the space instead. Page-scroll (the default)
   * is right for form screens — see the web implementation's header comment.
   * Native ignores this.
   * @default false
   */
  contained?: boolean;
  children: ReactNode;
};

/**
 * Partitions footer children by PrimaryAction identity.
 *
 * Returns items before the primary in `above` (never floated), the primary
 * element itself, and items after it in `below` (measured, covered by keyboard).
 * When no primary is found, all items land in `above` and the footer renders
 * flat with no sticky behaviour.
 */
export function partitionFooterChildren(children: ReactNode): {
  above: ReactNode[];
  primary: ReactNode | null;
  below: ReactNode[];
} {
  const items = Children.toArray(children);
  const primaryIndex = items.findIndex(c => isValidElement(c) && c.type === OnboardingPrimaryAction);
  if (primaryIndex === -1) {
    return {
      above: items,
      primary: null,
      below: []
    };
  }
  return {
    above: items.slice(0, primaryIndex),
    primary: items[primaryIndex] ?? null,
    below: items.slice(primaryIndex + 1)
  };
}

/**
 * Computes the `KeyboardStickyView` opened offset for the primary button.
 *
 * When content sits below the primary, the primary's natural resting position
 * is raised by `spaceBelow + SLOT_GAP`; those are added back so the button
 * parks at the same distance above the keyboard regardless of below-content.
 */
export function computeOpenedOffset(spaceBelow: number, hasBelow: boolean): number {
  return FOOTER_KEYBOARD_OFFSET + (hasBelow ? spaceBelow + SLOT_GAP : 0);
}

/**
 * Nav slot. Callers should always render `<OnboardingScreen.Nav />` so the
 * slot reserves the same `$si7` vertical space whether or not a progress
 * bar or back button is shown. On web the Granted wordmark renders by
 * default — pass `showLogo={false}` on screens that should suppress it.
 */
export function OnboardingNav({
  progress,
  onBackPress,
  title,
  showLogo,
  tone,
  onClosePress
}: OnboardingNavProps) {
  const rootTone = useContext(OnboardingToneContext);
  return <OnboardingProgressHeader progress={progress} onBackPress={onBackPress} title={title} showLogo={showLogo} tone={tone ?? rootTone} onClosePress={onClosePress} data-sentry-element="OnboardingProgressHeader" data-sentry-component="OnboardingNav" data-sentry-source-file="OnboardingScreen.shared.tsx" />;
}

/**
 * Header region. Designed to be rendered as the first child of
 * `<OnboardingScreen.Content>` — horizontal padding and the top inset are
 * owned by `Content`, so rendering `<Header>` outside of `<Content>` is
 * unsupported.
 */
export function OnboardingHeader(props: OnboardingHeaderProps) {
  const {
    title,
    subtitle,
    alignment = "center",
    testID
  } = props;
  const isLeft = alignment === "left";
  const textAlign = isLeft ? "left" : "center";
  const isDark = useContext(OnboardingToneContext) === "dark";
  return <YStack paddingBottom="$sp8" gap="$sp1.5" alignItems={isLeft ? "flex-start" : "center"} testID={testID} data-sentry-element="YStack" data-sentry-component="OnboardingHeader" data-sentry-source-file="OnboardingScreen.shared.tsx">
      {props.showLogo ?
    // The raster mark can't be recoloured, so dark takes the SVG one.
    isDark ? <GrantedGLogo height={LOGO_SIZE} width={LOGO_SIZE} color="$iconInverse" /> : <NewGLogo height={LOGO_SIZE} width={LOGO_SIZE} /> : null}
      {props.badge ? <Badge status="positive" size="medium">
          {props.badge}
        </Badge> : null}
      <Display2 color={isDark ? "$textInverse" : undefined} textAlign={textAlign} data-sentry-element="Display2" data-sentry-source-file="OnboardingScreen.shared.tsx">
        {title}
      </Display2>
      {subtitle ? <Body1 color={isDark ? "$textInverseSubtle" : "$textSubtle"} textAlign={textAlign}>
          {subtitle}
        </Body1> : null}
    </YStack>;
}
export function ContentBody({
  children,
  flushBottom = false
}: {
  children: ReactNode;
  /** Drops the bottom padding for a parent scroller that carries it instead. */
  flushBottom?: boolean;
}) {
  return <YStack flex={1} paddingTop="$sp4" paddingBottom={flushBottom ? 0 : "$sp6"} paddingHorizontal="$sp2.5" data-sentry-element="YStack" data-sentry-component="ContentBody" data-sentry-source-file="OnboardingScreen.shared.tsx">
      {children}
    </YStack>;
}

// Shared action-strip container: paddings, slot gap, and the web fade transition.
export function FooterStrip({
  isFading,
  children
}: {
  isFading?: boolean;
  children: ReactNode;
}) {
  return <YStack paddingHorizontal="$sp2.5" gap={FOOTER_SLOT_GAP_TOKEN} opacity={isFading ? 0 : 1} transition="300ms ease" data-sentry-element="YStack" data-sentry-component="FooterStrip" data-sentry-source-file="OnboardingScreen.shared.tsx">
      {children}
    </YStack>;
}
export function OnboardingPrimaryAction({
  variant = "primary",
  compact = false,
  ...rest
}: OnboardingPrimaryActionProps) {
  return <Button variant={variant} size="large" fillWidth={!compact} alignSelf={compact ? "flex-end" : undefined} {...rest} data-sentry-element="Button" data-sentry-component="OnboardingPrimaryAction" data-sentry-source-file="OnboardingScreen.shared.tsx" />;
}
export function OnboardingSecondaryAction(props: OnboardingButtonProps) {
  return <Button variant="secondary" size="large" fillWidth {...props} data-sentry-element="Button" data-sentry-component="OnboardingSecondaryAction" data-sentry-source-file="OnboardingScreen.shared.tsx" />;
}
export function OnboardingTertiaryAction(props: OnboardingButtonProps) {
  return <Button variant="tertiary" size="large" fillWidth {...props} data-sentry-element="Button" data-sentry-component="OnboardingTertiaryAction" data-sentry-source-file="OnboardingScreen.shared.tsx" />;
}
export function OnboardingDisclaimer({
  children
}: OnboardingDisclaimerProps) {
  return <YStack alignItems="center" data-sentry-element="YStack" data-sentry-component="OnboardingDisclaimer" data-sentry-source-file="OnboardingScreen.shared.tsx">{children}</YStack>;
}

/**
 * Form-level (non-field) error slot. Render inside `<Footer>` above the
 * `<PrimaryAction>` — general submit failures belong at the bottom of the
 * form, never annotated onto an individual field.
 */
export function OnboardingFormError({
  message
}: OnboardingFormErrorProps) {
  return message !== null ? <Caption color="$textNegative" textAlign="center" role="alert" aria-live="polite" data-sentry-element="Caption" data-sentry-component="OnboardingFormError" data-sentry-source-file="OnboardingScreen.shared.tsx">
      {message}
    </Caption> : null;
}